import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Container } from "@/components/Container/Container";
import { Section } from "@/components/Section/Section";
import { Reveal } from "@/components/Reveal/Reveal";
import { Button } from "@/components/Button/Button";
import { trackEvent } from "@/lib/analytics";
import {
  contact,
  contactEmail,
  SUBMIT_LEAD_URL,
} from "@/spiel/contact";
import type { ContactFormValues } from "@/spiel/types";
import styles from "./Contact.module.scss";

const submitLead = async (values: ContactFormValues): Promise<void> => {
  const response = await fetch(SUBMIT_LEAD_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(values),
  });

  if (!response.ok) {
    throw new Error("Submit failed");
  }
};

export const Contact = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ContactFormValues>();

  const mutation = useMutation({
    mutationFn: submitLead,
    onSuccess: () => {
      trackEvent("generate_lead");
      reset();
    },
  });

  const onSubmit = (values: ContactFormValues) => {
    mutation.mutate(values);
  };

  return (
    <Section id="contact" deep>
      <Container narrow>
        <Reveal>
          <h2 className={styles.heading}>{contact.heading}</h2>
          <p className={styles.subheading}>{contact.subheading}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <form className={styles.form} onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className={styles.field}>
              <label htmlFor="name" className={styles.label}>
                {contact.fields.name.label}
              </label>
              <input
                id="name"
                className={styles.input}
                placeholder={contact.fields.name.placeholder}
                {...register("name", { required: true })}
              />
              {errors.name && (
                <span className={styles.error}>{contact.fieldRequired}</span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="email" className={styles.label}>
                {contact.fields.email.label}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                inputMode="email"
                className={styles.input}
                placeholder={contact.fields.email.placeholder}
                {...register("email", {
                  required: true,
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                })}
              />
              {errors.email?.type === "required" && (
                <span className={styles.error}>{contact.fieldRequired}</span>
              )}
              {errors.email?.type === "pattern" && (
                <span className={styles.error}>
                  {contact.fieldEmailInvalid}
                </span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="situation" className={styles.label}>
                {contact.fields.situation.label}
              </label>
              <textarea
                id="situation"
                className={styles.textarea}
                rows={4}
                placeholder={contact.fields.situation.placeholder}
                {...register("situation", { required: true })}
              />
              {errors.situation && (
                <span className={styles.error}>{contact.fieldRequired}</span>
              )}
            </div>

            <div className={styles.field}>
              <label htmlFor="timeline" className={styles.label}>
                {contact.fields.timeline.label}
              </label>
              <input
                id="timeline"
                className={styles.input}
                placeholder={contact.fields.timeline.placeholder}
                {...register("timeline", { required: true })}
              />
              {errors.timeline && (
                <span className={styles.error}>{contact.fieldRequired}</span>
              )}
            </div>

            <Button
              type="submit"
              className={styles.submit}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? contact.sending : contact.submit}
            </Button>

            {mutation.isSuccess && (
              <p className={styles.feedback}>{contact.success}</p>
            )}

            {mutation.isError && (
              <p className={styles.feedbackError}>
                {contact.error}{" "}
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
              </p>
            )}
          </form>
        </Reveal>
      </Container>
    </Section>
  );
};
